import { isToday, set } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

import { createInstance } from '@/helpers/api/base'
import { ICreateControllerLogRequest, Log } from '@/types/logs'
import { useNotification } from '@/ui/Notification'
import { useQueryClient } from '@tanstack/react-query'
import { useAppMutation } from '@/helpers/api/mutation'
import { useGetUserInfo } from '@/modules/Auth'
import { useGetReference } from './useGetReference'
import { useGetControllerLogParams } from '../apiHelpers/useGetControllerLogParams'
import { formatLogRequestBody } from '../helpers/formatLogRequestBody'
import { useGetCodes } from './useGetCodes'
import { useDateStore } from '@/components/DateInput'
import { getCodeTypeId } from '../helpers/getCodeTypeId'

export const useDuplicateControllerLog = (log: Log) => {
    const { date } = useDateStore()
    const queryClient = useQueryClient()
    const { showNotification } = useNotification()
    const { data: userInfo } = useGetUserInfo()
    const { data: reference } = useGetReference()
    const logParams = useGetControllerLogParams()
    const { data: codes = [] } = useGetCodes()

    const duplicateLogMutation = useAppMutation<Log, Error, ICreateControllerLogRequest, Log[]>({
        mutationFn: async (data) =>
            createInstance({
                url: '/controller-logs',
                method: 'POST',
                data,
            }),
        alertOnError: true,
        queryKey: ['controllerLogs', ...logParams],
        optimisticChangesCallback: (requestBody: ICreateControllerLogRequest, previousLogs?: Log[]) => [
            ...(previousLogs || []),
            formatLogRequestBody({ requestBody, codes }),
        ],
        onSuccess: async () => {
            showNotification({ message: 'Record duplicated' })
        },
    })

    const handleDuplicateLog = async () => {
        const {
            mechanicalInfo,
            report,
            descriptionInfo,
            userShift,
            lastChangedAt,
            status,
            source,
            version,
            eventLog,
            delayInMinutes,
            codeTypeReasonInfo,
            trip,
            rtnTrips,
            originalTrip,
            run,
            duty,
            fleet,
            bulkCurtailment,
            missingFields,
            isUpdatedAfterUnfinished,
            unfinishedFlagMetadata,
            codingPeriodInfo,
            logType,
            ...rest
        } = log

        const now = new Date()
        const dateWithCurrentTime = set(date, {
            hours: now.getHours(),
            minutes: now.getMinutes(),
            seconds: now.getSeconds(),
            milliseconds: now.getMilliseconds(),
        })

        const requestBody = {
            ...rest,
            ...descriptionInfo,
            id: crypto.randomUUID(),
            codeTypeId: getCodeTypeId(rest.code),
            reference,
            delay: delayInMinutes ? delayInMinutes.toString() : undefined,
            author: userInfo?.lastSessionDetails.authorName,
            timestamp: formatInTimeZone(dateWithCurrentTime, 'UTC', "yyyy-MM-dd'T'HH:mm:ss'Z'"),
            eventLogId: eventLog && isToday(eventLog.timestamp) ? eventLog?.id : undefined,
            selectedCodeTypeReasonId: codeTypeReasonInfo ? codeTypeReasonInfo.reasonId : undefined,
        } as unknown as ICreateControllerLogRequest

        // Reset reference code before sending request
        await queryClient.invalidateQueries({ queryKey: ['reference'] })

        duplicateLogMutation.mutate(requestBody)
    }

    return {
        handleDuplicateLog,
    }
}
